import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import {
    BaseControl,
    provideValidator,
    provideValueAccessor,
} from '@lib/frontend-shared-ui-ionic-form-utilities';
import { ALL_GRADE_LEVELS, GradeLevel } from '@lib/shared-util-global-constants';
import {
    AbstractControl,
    ControlValueAccessor,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'lib-grade-levels-select',
    standalone: true,
    imports: [IonicModule, ReactiveFormsModule, TranslateModule],
    templateUrl: './grade-levels-select.control.html',
    styleUrl: './grade-levels-select.control.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        provideValueAccessor(() => GradeLevelsSelectControl),
        provideValidator(() => GradeLevelsSelectControl),
    ],
})
export class GradeLevelsSelectControl
    extends BaseControl<GradeLevel[] | GradeLevel | undefined>
    implements ControlValueAccessor, OnInit, Validator
{
    @Input() public label = 'Grades Taught';
    @Input() public options = ALL_GRADE_LEVELS;
    @Input() public multiple = false;
    @Input() public interface?: string = undefined;

    protected readonly gradeLevels = this.formBuilder.control<
        GradeLevel[] | GradeLevel | undefined
    >(undefined);

    private externalControl?: AbstractControl;

    public constructor(private readonly formBuilder: NonNullableFormBuilder) {
        super();
    }

    public ngOnInit(): void {
        this.gradeLevels.valueChanges.subscribe((gradeLevel) => this.handleChange(gradeLevel));
    }

    public writeValue(gradeLevels: GradeLevel[] | GradeLevel | undefined) {
        this.gradeLevels.setValue(gradeLevels);
    }

    public override setDisabledState(isDisabled: boolean) {
        this.gradeLevels[isDisabled ? 'disable' : 'enable']();
    }

    public validate(externalControl: AbstractControl): ValidationErrors | null {
        this.mirrorExternalValidator(externalControl);

        // eslint-disable-next-line unicorn/no-null
        return null;
    }

    private handleChange(gradeLevels: GradeLevel[] | GradeLevel | undefined) {
        this.gradeLevels.setValue(gradeLevels, { emitEvent: false });
        this.notifyChanged?.(gradeLevels);
    }

    private mirrorExternalValidator(externalControl: AbstractControl) {
        // We only need to do this one time.
        if (this.externalControl) return;

        this.externalControl = externalControl;

        const externalControlValidator = externalControl.validator;
        if (!externalControlValidator) return;

        // This ensures the control shows red on blur if no edits were made.
        this.gradeLevels.addValidators(externalControlValidator);
    }
}
